import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { finalize, Subscription, tap } from 'rxjs';
import { HttpClientModule } from '@angular/common/http';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { QuizAssessmentLandingPageDetails } from '@edtech-ui/core/models/quiz-landing-page/get-all-quiz-link';
import { DeepLinkUtil } from '@edtech-ui/core/utils/deep-link.util';

@Component({
	selector: 'app-featured-mock-test',
	standalone: true,
	imports: [CommonModule, HttpClientModule, RouterModule],
	templateUrl: './featured-mock-test.component.html',
	styleUrl: './featured-mock-test.component.scss'
})
export class FeaturedMockTestComponent implements OnInit, OnDestroy {

	mockTest: QuizAssessmentLandingPageDetails[] = [];
	isLoading = false;
	isFetchingMore = false;
	currentPage = 1;
	hasMoreTests = true;
	isAtStart = true;
	isAtEnd = false;

	private readonly PAGE_SIZE = 6;
	private subscription = new Subscription();
	private scrollTimeout: any = null;
	private readonly SCROLL_DEBOUNCE_MS = 150;

	isBrowser: boolean;
	isDashboard = false;
	@ViewChild('testsGrid') testsGrid!: ElementRef;


	constructor(
		private quizService: QuizManagementService,
		@Inject(PLATFORM_ID) platformId: Object,
		private router: Router
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}


	@HostListener('window:resize')
	onWindowResize() {
		if (!this.isBrowser) return;
		this.debouncedScrollCheck();
	}


	ngOnInit() {
		this.isDashboard = !!localStorage.getItem('token');
		this.getQuizAssessmentLinks();
	}


	getQuizAssessmentLinks() {
		this.isLoading = true;
		this.currentPage = 1;
		this.hasMoreTests = true;
		this.mockTest = [];

		this.subscription.add(
			this.quizService.getQuizAssessmentLandingPage(
				this.currentPage, this.PAGE_SIZE, true, undefined, undefined,
			).pipe(
				tap(response => {
					if (response.Success) {
						this.mockTest = response.Data.data || [];
						this.hasMoreTests = this.calculateHasMore(response.Data);
					} else {
						this.mockTest = [];
						this.hasMoreTests = false;
					}
					this.isLoading = false;
					setTimeout(() => this.checkScrollPosition(), 100);
				}), finalize(() => {
					this.isLoading = false;
				})
			).subscribe()
		);
	}


	loadNextPage() {
		if (!this.hasMoreTests || this.isFetchingMore || this.isLoading) return;
		this.isFetchingMore = true;
		const nextPage = this.currentPage + 1;

		this.subscription.add(
			this.quizService.getQuizAssessmentLandingPage(
				nextPage, this.PAGE_SIZE, true, undefined, undefined,
			).subscribe({
				next: (res: any) => {
					if (res?.Success && res?.Data?.data) {
						const newQuizzes: QuizAssessmentLandingPageDetails[] = res.Data.data;
						if (newQuizzes.length > 0) {
							const existingIds = new Set(this.mockTest.map(t => t.link_id));
							const uniqueNewTests = newQuizzes.filter(t => !existingIds.has(t.link_id));
							if (uniqueNewTests.length > 0) {
								this.mockTest = [...this.mockTest, ...uniqueNewTests];
							}
						}
						this.currentPage = nextPage;
						this.hasMoreTests = this.calculateHasMore(res.Data);
					} else {
						this.hasMoreTests = false;
					}
					this.isFetchingMore = false;
					setTimeout(() => this.checkScrollPosition(), 100);
				},
				error: () => {
					this.isFetchingMore = false;
				}
			})
		);
	}


	private calculateHasMore(data: any): boolean {
		if (!data) return false;
		if (data.pagination) {
			return data.pagination.hasNextPage;
		}
		if (data.totalPages && this.currentPage >= data.totalPages) return false;
		if (!data.data || data.data.length < this.PAGE_SIZE) return false;
		return true;
	}


	trackByTestId(index: number, test: any): string {
		return test?.link_id || test?._id || index.toString();
	}


	getCardScrollAmount(): number {
		if (!this.testsGrid?.nativeElement || !this.isBrowser) return 300;
		const cards = this.testsGrid.nativeElement.querySelectorAll('.mock-test-card');
		if (cards.length >= 2) {
			const diff = cards[1].offsetLeft - cards[0].offsetLeft;
			return diff > 0 ? diff : 300;
		} else if (cards.length === 1) {
			return cards[0].offsetWidth + 24;
		}
		return 300;
	}


	scrollLeft() {
		if (!this.testsGrid?.nativeElement || !this.isBrowser) return;
		const scrollAmount = this.getCardScrollAmount();
		this.testsGrid.nativeElement.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
		setTimeout(() => this.checkScrollPosition(), 400);
	}


	scrollRight() {
		if (!this.testsGrid?.nativeElement || !this.isBrowser) return;
		const scrollAmount = this.getCardScrollAmount();
		this.testsGrid.nativeElement.scrollBy({ left: scrollAmount, behavior: 'smooth' });
		setTimeout(() => this.checkScrollPosition(), 400);
	}


	onGridScroll() {
		this.debouncedScrollCheck();
	}


	private debouncedScrollCheck() {
		if (this.scrollTimeout) {
			clearTimeout(this.scrollTimeout);
		}
		this.scrollTimeout = setTimeout(() => {
			this.checkScrollPosition();
			this.scrollTimeout = null;
		}, this.SCROLL_DEBOUNCE_MS);
	}


	checkScrollPosition() {
		if (!this.testsGrid?.nativeElement || !this.isBrowser) return;
		const el = this.testsGrid.nativeElement;
		this.isAtStart = el.scrollLeft <= 5;
		const maxScroll = el.scrollWidth - el.clientWidth;
		this.isAtEnd = maxScroll <= 0 || el.scrollLeft >= maxScroll - 5;
		if (el.scrollLeft >= maxScroll - 100 && this.hasMoreTests && !this.isFetchingMore && !this.isLoading) {
			this.loadNextPage();
		}
	}


	getTestDuration(test: QuizAssessmentLandingPageDetails): string {
		// time_limit is stored in seconds.
		const totalSeconds = test?.time_limit;
		if (!totalSeconds || totalSeconds <= 0) {
			return '';
		}
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		const parts: string[] = [];
		if (hours > 0) parts.push(`${hours} hr`);
		if (minutes > 0) parts.push(`${minutes} min`);
		if (seconds > 0) parts.push(`${seconds} sec`);

		return parts.join(' ');
	}


	startMockTest(test: QuizAssessmentLandingPageDetails) {
		if (DeepLinkUtil.isAndroid()) {
			window.location.href = DeepLinkUtil.getAndroidIntentUrl('student-dashboard/placement-prep');
			return;
		}

		const token = localStorage.getItem('token');
		if (!token) {
			this.router.navigate(['/login'], { queryParams: { redirectUrl: '/student-dashboard/placement-prep' } });
			return;
		}
		this.router.navigate(['/my-assessment', test.link_id || test._id], { queryParams: { isStudentDashboard: 'true', fromMockTest: 'true' } });
	}


	ngOnDestroy() {
		this.subscription.unsubscribe();
		if (this.scrollTimeout) {
			clearTimeout(this.scrollTimeout);
			this.scrollTimeout = null;
		}
	}

}
